// ===============================================================
// File Library — Publish (Create / Edit Document)
// Form for creating new documents or editing existing ones
// ===============================================================

import { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Title, Text, Paper, TextInput, Textarea, Select, Button,
  Group, Stack, SegmentedControl, SimpleGrid,
} from '../../components/gab/GabComponents';
import { notifications } from '../../lib/notifications';
import { IconUpload, IconLink, IconDeviceFloppy } from '@tabler/icons-react';
import { useFileLibraryStore } from '../../store/fileLibraryStore';
import type { FLDocument } from '../../store/fileLibraryStore';
import { useAuthStore } from '../../store/authStore';

const ACCEPTED_TYPES = '.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.zip,.csv';

function detectFileType(filename: string): string {
  const ext = filename.split('.').pop()?.toLowerCase() ?? '';
  return ext;
}

function todayString(): string {
  return new Date().toISOString().slice(0, 10);
}

export default function Publish() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { documents, categories, loadDocuments, loadCategories, createDocument, updateDocument, uploadFile } = useFileLibraryStore();
  const { user } = useAuthStore();

  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [category, setCategory] = useState('');
  const [docSource, setDocSource] = useState<string>('upload');
  const [fileUrl, setFileUrl] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [publishDate, setPublishDate] = useState(todayString());
  const [expirationDate, setExpirationDate] = useState('');
  const [version, setVersion] = useState('1.0');
  const [priority, setPriority] = useState('Normal');
  const [tags, setTags] = useState('');
  const [uploadedBy, setUploadedBy] = useState(user?.fullName ?? '');
  const [status, setStatus] = useState('Published');
  const [saving, setSaving] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    // Only reload documents if editing and we don't have them yet
    if (isEdit && documents.length === 0) loadDocuments();
    if (categories.length === 0) loadCategories();
  }, [isEdit, documents.length, categories.length, loadDocuments, loadCategories]);

  // Pre-fill form when editing
  useEffect(() => {
    if (!isEdit || !id) return;
    const doc = documents.find(d => String(d.id) === id);
    if (!doc) return;
    setTitle(doc.title);
    setDescription(doc.description);
    setCategory(doc.category);
    setFileUrl(doc.fileUrl);
    setPublishDate(doc.publishDate ? doc.publishDate.slice(0, 10) : todayString());
    setExpirationDate(doc.expirationDate ? doc.expirationDate.slice(0, 10) : '');
    setVersion(doc.version || '1.0');
    setPriority(doc.priority || 'Normal');
    setTags(doc.tags);
    setUploadedBy(doc.uploadedBy);
    setStatus(doc.status || 'Published');
    setDocSource(doc.fileUrl ? 'link' : 'upload');
  }, [isEdit, id, documents]);

  const categoryOptions = (() => {
    const seen = new Set<string>();
    return categories
      .filter(c => c.active === 'true' || c.active === 'Yes')
      .sort((a, b) => (Number(a.sortOrder) || 99) - (Number(b.sortOrder) || 99))
      .flatMap(c => {
        const key = String(c.id);
        if (!c.name || seen.has(key)) return [];
        seen.add(key);
        return [{ value: key, label: c.name }];
      });
  })();

  const handleSave = async () => {
    if (!title.trim()) {
      notifications.show({ title: 'Required', message: 'Enter a document title.', color: 'var(--color-error)' });
      return;
    }

    setSaving(true);
    try {
      let fileType = '';
      if (docSource === 'upload' && selectedFile) {
        fileType = detectFileType(selectedFile.name);
      } else if (docSource === 'link' && fileUrl) {
        fileType = detectFileType(fileUrl);
      }

      const data: Partial<FLDocument> = {
        title: title.trim(),
        description: description.trim(),
        category,
        fileUrl: docSource === 'link' ? fileUrl.trim() : '',
        fileType,
        publishDate,
        expirationDate,
        version: version.trim(),
        priority,
        tags: tags.trim(),
        uploadedBy: uploadedBy.trim(),
        status,
      };

      if (isEdit && id) {
        await updateDocument(Number(id), data);
        if (docSource === 'upload' && selectedFile) {
          await uploadFile(Number(id), selectedFile);
        }
        notifications.show({ title: 'Updated', message: 'Document updated successfully.', color: 'var(--color-success)' });
      } else {
        const newDoc = await createDocument(data);
        if (newDoc && docSource === 'upload' && selectedFile) {
          await uploadFile(newDoc.id, selectedFile);
        }
        notifications.show({ title: 'Published', message: 'Document published successfully.', color: 'var(--color-success)' });
      }

      navigate('/filelibrary/browse');
    } catch (err) {
      notifications.show({
        title: 'Error',
        message: err instanceof Error ? err.message : 'Failed to save document.',
        color: 'var(--color-error)',
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <Title order={3} mb="md" c="black">{isEdit ? 'Edit Document' : 'Publish Document'}</Title>

      <SimpleGrid cols={{ base: 1, md: 2 }} mb="md">
        {/* Left column — primary fields */}
        <Paper p="lg" radius="sm" withBorder>
          <Stack gap="sm">
            <TextInput
              label="Title"
              placeholder="Document title"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
            />

            <Textarea
              label="Description"
              placeholder="Brief description of the document..."
              value={description}
              onChange={e => setDescription(e.target.value)}
              minRows={3}
              autosize
            />

            <Select
              label="Category"
              placeholder="Select category"
              data={categoryOptions}
              value={category}
              onChange={v => setCategory(v ?? '')}
              searchable
            />

            <div>
              <Text size="sm" fw={500} mb={4}>Document Source</Text>
              <SegmentedControl
                value={docSource}
                onChange={setDocSource}
                data={[
                  { value: 'upload', label: 'Upload File' },
                  { value: 'link', label: 'External Link' },
                ]}
                size="xs"
                fullWidth
              />
            </div>

            {docSource === 'upload' ? (
              <div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept={ACCEPTED_TYPES}
                  style={{ display: 'none' }}
                  onChange={e => {
                    const f = e.target.files?.[0] ?? null;
                    setSelectedFile(f);
                  }}
                />
                <Button
                  variant="outline"
                  leftSection={<IconUpload size={14} />}
                  onClick={() => fileInputRef.current?.click()}
                  size="xs"
                >
                  {selectedFile ? 'Change File' : 'Choose File'}
                </Button>
                {selectedFile && (
                  <Text size="xs" c="dimmed" mt={4}>
                    {selectedFile.name} ({(selectedFile.size / 1024).toFixed(1)} KB)
                  </Text>
                )}
              </div>
            ) : (
              <TextInput
                label="URL"
                placeholder="https://drive.google.com/... or SharePoint link"
                leftSection={<IconLink size={14} />}
                value={fileUrl}
                onChange={e => setFileUrl(e.target.value)}
              />
            )}

            <TextInput
              label="Tags"
              placeholder="Comma-separated tags (e.g. policy, safety, training)"
              value={tags}
              onChange={e => setTags(e.target.value)}
            />
          </Stack>
        </Paper>

        {/* Right column — metadata */}
        <Paper p="lg" radius="sm" withBorder>
          <Stack gap="sm">
            <TextInput
              label="Publish Date"
              type="date"
              value={publishDate}
              onChange={e => setPublishDate(e.target.value)}
            />

            <TextInput
              label="Expiration Date"
              type="date"
              value={expirationDate}
              onChange={e => setExpirationDate(e.target.value)}
              description="Leave blank if no expiration"
            />

            <TextInput
              label="Version"
              placeholder="e.g. 1.0"
              value={version}
              onChange={e => setVersion(e.target.value)}
            />

            <Select
              label="Priority"
              data={[
                { value: 'Normal', label: 'Normal' },
                { value: 'Important', label: 'Important' },
                { value: 'Critical', label: 'Critical' },
              ]}
              value={priority}
              onChange={v => setPriority(v ?? 'Normal')}
            />

            <TextInput
              label="Uploaded By"
              value={uploadedBy}
              onChange={e => setUploadedBy(e.target.value)}
            />

            <Select
              label="Status"
              data={[
                { value: 'Published', label: 'Published' },
                { value: 'Draft', label: 'Draft' },
              ]}
              value={status}
              onChange={v => setStatus(v ?? 'Published')}
            />

            <Group justify="flex-end" mt="md">
              <Button variant="subtle" onClick={() => {
                // Warn if there's unsaved work in the form
                const hasUnsavedWork = !!(title.trim() || description.trim() || fileUrl.trim());
                if (hasUnsavedWork && !confirm('Discard unsaved changes?')) return;
                navigate('/filelibrary/browse');
              }}>Cancel</Button>
              <Button
                leftSection={<IconDeviceFloppy size={14} />}
                onClick={handleSave}
                loading={saving}
              >
                {isEdit ? 'Update Document' : 'Publish Document'}
              </Button>
            </Group>
          </Stack>
        </Paper>
      </SimpleGrid>
    </div>
  );
}
